import { Link, useLocation, useNavigate } from "react-router-dom";

import { site } from "../../docs.config.js";
import { type NavSection } from "../../types/content.ts";
import { firstPageOf, visibleNav } from "../lib/content.js";
import { useLocale } from "../lib/i18n.jsx";
import Icon from "../theme/Icon.jsx";
import { LogoMark } from "../theme/Logo.jsx";
import ThemeToggle from "../theme/ThemeToggle.jsx";

// Destino de cada item do menu. Seções de guias apontam para a primeira página
// da sidebar; as demais para a própria seção, que resolve o resto.
function sectionHref(locale: string, item: NavSection): string {
  if (item.type === "guides") {
    const page = firstPageOf(locale, item.id);
    return page ? `/${locale}/${item.id}/${page.slug}` : `/${locale}/${item.id}`;
  }
  return `/${locale}/${item.id}`;
}

export default function Subnav({ activeSection, onSearch }: { activeSection?: string; onSearch?: () => void }) {
  const { locale, t, label, available } = useLocale();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  // Trocar de idioma mantém a página: só o primeiro segmento da URL muda.
  const switchLocale = (code: string) => {
    const parts = pathname.split("/").filter(Boolean);
    parts[0] = code;
    navigate(`/${parts.join("/")}`);
  };

  return (
    <div className="docs-subnav">
      <div className="docs-subnav-inner">
        <a href={site.homeUrl} className="docs-subnav-brand" aria-label={t("backToSite")}>
          <LogoMark height={18} />
        </a>

        <nav className="docs-subnav-menu" aria-label={t("navigation")}>
          {visibleNav.map((item) => (
            <Link
              key={item.id}
              to={sectionHref(locale, item)}
              className={`docs-subnav-item ${activeSection === item.id ? "active" : ""} ${item.ai ? "ai" : ""}`}
            >
              <Icon name={item.icon ?? "book"} size={14} />
              <span>{label(item.label)}</span>
            </Link>
          ))}
        </nav>

        <div className="docs-subnav-right">
          <button type="button" className="docs-subnav-search" onClick={onSearch}>
            <Icon name="search" size={14} />
            <span>{t("search")}</span>
            <kbd>⌘K</kbd>
          </button>
          <ThemeToggle />
          <div className="lang-switch">
            {available.map((l) => (
              <button
                key={l.code}
                type="button"
                title={l.name}
                className={`lang-btn ${locale === l.code ? "active" : ""}`}
                onClick={() => switchLocale(l.code)}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
